// src/pages/SharedReport.js
// Page publique d'un rapport partagé par une agence : /rapport/:token. Aux couleurs de l'agence, sans compte.
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, AlertTriangle, Info, Loader2, Download, Mail, Globe } from 'lucide-react';
import { findExplanation } from '../data/auditExplanations';
import { splitSections, downloadAuditPDF } from '../utils/pdfGenerator';

const API_URL = 'https://cyberguard-api-ka8f.onrender.com';
const GRADES = { A: ['#22c55e', 'Excellent'], B: ['#84cc16', 'Très bon'], C: ['#eab308', 'Bon'], D: ['#f97316', 'Moyen'], E: ['#ef4444', 'Insuffisant'], F: ['#dc2626', 'Faible'], G: ['#991b1b', 'Critique'] };
const safeColor = (c) => (/^#[0-9a-fA-F]{6}$/.test(c || '') ? c : '#3b82f6');
const safeUrl = (u) => (/^https?:\/\//.test(u || '') ? u : null);
const clean = (l) => l.replace(/^(✅|❌|⚠️|ℹ️)\s*/, '');

function Line({ line }) {
  const [open, setOpen] = useState(false);
  const exp = findExplanation(line);
  const Icon = line.startsWith('✅') ? CheckCircle : line.startsWith('❌') ? XCircle : line.startsWith('⚠️') ? AlertTriangle : Info;
  const color = line.startsWith('✅') ? 'text-green-600' : line.startsWith('❌') ? 'text-red-600' : line.startsWith('⚠️') ? 'text-amber-600' : 'text-zinc-400';
  return (
    <li className="py-3">
      <button type="button" onClick={() => exp && setOpen((o) => !o)} className={`w-full flex items-start gap-3 text-left ${exp ? 'cursor-pointer' : 'cursor-default'}`} aria-expanded={exp ? open : undefined}>
        <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${color}`} />
        <span className="flex-1 text-sm text-zinc-800">{clean(line)}</span>
        {exp && <span className="text-xs text-zinc-400 shrink-0">{open ? 'Masquer' : 'Explication'}</span>}
      </button>
      {exp && open && <p className="ml-8 mt-2 text-sm text-zinc-600 bg-zinc-50 border border-zinc-200 p-3">{exp.simple}</p>}
    </li>
  );
}

export default function SharedReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    // Page privée : ni indexation, ni lien transmis aux sites tiers.
    let meta = document.querySelector('meta[name="robots"]');
    const created = !meta;
    const previous = meta?.getAttribute('content');
    if (created) { meta = document.createElement('meta'); meta.setAttribute('name', 'robots'); document.head.appendChild(meta); }
    meta.setAttribute('content', 'noindex, nofollow');
    axios.get(`${API_URL}/api/public/reports/${encodeURIComponent(token)}`)
      .then((r) => {
        setData(r.data);
        document.title = `Rapport de sécurité — ${r.data.audit.target_domain}`;
      })
      .catch((err) => setError(err.response?.data?.detail || 'Ce rapport est introuvable.'));
    return () => { if (created) meta.remove(); else meta.setAttribute('content', previous); };
  }, [token]);

  if (error) return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-6">
      <div className="bg-white border border-zinc-200 p-8 max-w-md text-center space-y-3" data-testid="shared-error">
        <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto" />
        <h1 className="font-bold text-lg text-zinc-900">Rapport indisponible</h1>
        <p className="text-zinc-600 text-sm">{error}</p>
      </div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center gap-2 text-zinc-500">
      <Loader2 className="w-5 h-5 animate-spin" /> Chargement du rapport…
    </div>
  );

  const { audit, report, branding, client_name: clientName } = data;
  const brand = safeColor(branding.primary_color);
  const [gradeColor, gradeLabel] = GRADES[audit.dpe_grade] || GRADES.D;
  const sections = splitSections((audit.scan_details || []).filter((l) => !l.startsWith('🔒')));
  const v = audit.vulnerabilities || {};
  const site = safeUrl(branding.website);
  const company = branding.company_name || 'CyberGuard';

  const pdf = async () => {
    setPdfBusy(true);
    try { await downloadAuditPDF(audit, { branding, report, clientName }); } finally { setPdfBusy(false); }
  };

  const blocks = [['Sécurité du site', sections.security], ['Emails et DNS', sections.dns], ['Analyse approfondie', sections.deep], ['Conformité RGPD et mentions légales', sections.rgpd]];

  return (
    <div className="min-h-screen bg-zinc-100 text-zinc-900 shared-report" data-testid="shared-report">
      <div style={{ height: 6, background: brand }} />
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-4 py-5 flex items-center justify-between gap-4">
          {branding.logo
            ? <img src={branding.logo} alt={company} className="max-h-12 max-w-[180px] object-contain" />
            : <span className="font-bold text-xl" style={{ color: brand }}>{company}</span>}
          <button onClick={pdf} disabled={pdfBusy} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60" style={{ background: brand }}>
            {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} PDF
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <section className="bg-white border border-zinc-200 p-6">
          <p className="text-xs uppercase tracking-widest text-zinc-500">Rapport d'audit de sécurité{clientName ? ` · ${clientName}` : ''}</p>
          <h1 className="text-2xl sm:text-3xl font-black mt-1 break-all">{audit.target_domain}</h1>
          <p className="text-sm text-zinc-500 mt-1">Audit du {new Date(audit.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
          <div className="mt-6 flex flex-wrap items-center gap-6">
            <div className="w-16 h-16 flex items-center justify-center text-3xl font-black text-white" style={{ background: gradeColor }}>{audit.dpe_grade}</div>
            <div>
              <div className="text-3xl font-black">{audit.security_score}<span className="text-base text-zinc-400">/100</span></div>
              <div className="text-sm text-zinc-500">Niveau de sécurité : {gradeLabel}</div>
            </div>
            <div className="flex gap-5 sm:ml-auto text-center">
              {[['Critiques', v.critical], ['Élevées', v.high], ['Moyennes', v.medium], ['Faibles', v.low]].map(([l, n]) => (
                <div key={l}><div className="text-xl font-bold">{n || 0}</div><div className="text-[11px] text-zinc-500">{l}</div></div>
              ))}
            </div>
          </div>
        </section>

        {report?.summary && (
          <section className="bg-white border border-zinc-200 p-6 space-y-3">
            <h2 className="font-bold text-lg">En résumé</h2>
            <p className="text-sm text-zinc-700 leading-relaxed">{report.summary}</p>
            {report.risk_comment && <p className="text-sm text-zinc-600 leading-relaxed">{report.risk_comment}</p>}
          </section>
        )}

        {blocks.filter(([, lines]) => lines.length).map(([title, lines]) => (
          <section key={title} className="bg-white border border-zinc-200">
            <h2 className="px-6 py-3 font-bold text-white text-sm" style={{ background: brand }}>{title}</h2>
            <ul className="px-6 divide-y divide-zinc-100">{lines.map((l, i) => <Line key={i} line={l} />)}</ul>
          </section>
        ))}

        {report?.recommendations?.length > 0 && (
          <section className="bg-white border border-zinc-200 p-6 space-y-4">
            <h2 className="font-bold text-lg">Actions recommandées</h2>
            <ol className="space-y-3">
              {report.recommendations.map((r, i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-6 h-6 shrink-0 flex items-center justify-center text-xs font-bold text-white" style={{ background: brand }}>{r.priority || i + 1}</span>
                  <div className="text-sm"><p className="font-semibold">{r.action}</p>{r.detail && <p className="text-zinc-600">{r.detail}</p>}</div>
                </li>
              ))}
            </ol>
          </section>
        )}

        <footer className="bg-white border border-zinc-200 p-6 text-sm text-zinc-600 space-y-2">
          <p className="font-semibold text-zinc-900">Rapport préparé par {company}</p>
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            {branding.contact_email && <a href={`mailto:${branding.contact_email}`} className="inline-flex items-center gap-1 hover:underline"><Mail className="w-4 h-4" /> {branding.contact_email}</a>}
            {site && <a href={site} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline"><Globe className="w-4 h-4" /> {site.replace(/^https?:\/\//, '')}</a>}
          </div>
          <p className="text-xs text-zinc-400 pt-2">Ce rapport décrit les éléments visibles publiquement le jour de l'audit. Il ne remplace pas un test d'intrusion.</p>
        </footer>
      </main>
    </div>
  );
}
